// NEW_FILE_JUSTIFIED: Import Policy dialog — file upload + AI extraction + review form, no existing equivalent
import { useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: components need convex Id type
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, Sparkles, AlertCircle, FileText, X, Play } from 'lucide-react';
import { useImportPolicy } from '@/hooks/useImportPolicy';
import { useImportReviewForm } from '@/hooks/useImportReviewForm';
import { cn } from '@/lib/utils';
import type {
  IImportPolicyDialogProps,
  IOrgInsuranceType,
  IExtractedPolicyData,
  IPolicyFieldValue,
  IInsuranceProvider,
} from '@/@types/insurance';

/**
 * Build fieldValues array from extracted data, mapping to Car Insurance field labels.
 */
function buildFieldValues(
  data: IExtractedPolicyData,
  providers: IInsuranceProvider[]
): IPolicyFieldValue[] {
  const fields: IPolicyFieldValue[] = [];

  const push = (label: string, value: string | undefined | null) => {
    if (value) fields.push({ fieldLabel: label, value });
  };

  push('Car registration number', data.vehicle?.registrationNumber);
  push(
    'Car model',
    [data.vehicle?.make, data.vehicle?.model].filter(Boolean).join(' ') || undefined
  );
  push('Home address', data.policyholder.address);
  push('Postal code', data.policyholder.postalCode);
  push('City', data.policyholder.city);
  push('Phone number', data.policyholder.phone);
  push('Email address', data.policyholder.email);
  push('Start date', data.insurance.startDate);
  push('Expiry date', data.insurance.expiryDate);

  // Policy type: map extracted insurance type to select options
  const insType = (data.insurance.type ?? '').toLowerCase();
  if (insType.includes('comprehensive')) {
    fields.push({ fieldLabel: 'Policy type', value: 'Comprehensive' });
    // For Comprehensive policies, Sum Insured = Car market price
    if (data.coverage?.sumInsured) {
      const numericValue = data.coverage.sumInsured.replace(/[^0-9.]/g, '');
      if (numericValue) push('Car market price', numericValue);
    }
  } else if (insType.includes('fire') && insType.includes('theft')) {
    fields.push({ fieldLabel: 'Policy type', value: 'Third Party with Fire and Theft' });
  } else if (insType.includes('third party') || insType.includes('tpl')) {
    fields.push({ fieldLabel: 'Policy type', value: 'Third Party' });
  }

  const durationText = (data.insurance.duration ?? '').toLowerCase();
  if (
    durationText.includes('annual') ||
    durationText.includes('12') ||
    durationText.includes('year')
  ) {
    fields.push({ fieldLabel: 'Duration (months)', value: '12' });
  } else if (durationText.includes('6') || durationText.includes('half')) {
    fields.push({ fieldLabel: 'Duration (months)', value: '6' });
  }

  fields.push({ fieldLabel: 'Vehicle registered on customer name', value: 'Yes' });

  if (data.insurer?.name && providers.length > 0) {
    const insurerLower = data.insurer.name.toLowerCase();
    const matched = providers.find(p => insurerLower.includes(p.name.toLowerCase()));
    if (matched) {
      fields.push({ fieldLabel: 'Provider', value: matched._id });
    }
  }

  return fields;
}

function findCarInsuranceType(types: IOrgInsuranceType[]): IOrgInsuranceType | undefined {
  return types.find(
    t =>
      t.name.toLowerCase().includes('car') ||
      (t.supportsDrivers && t.name.toLowerCase().includes('motor'))
  );
}

export function ImportPolicyDialog({ open, onOpenChange, orgId }: IImportPolicyDialogProps) {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { step, uploadedFileNames, extractedData, error, uploadFiles, savePolicy, reset } =
    useImportPolicy(orgId);

  const {
    selectedTypeId,
    policyNumber,
    customerName,
    pendingFiles,
    setTypeId,
    setPolicyNumber,
    setCustomerName,
    addFiles,
    removeFile,
    resetForm,
  } = useImportReviewForm();

  const orgTypes = useQuery(api['insurance/types'].listOrgTypes, { orgId });
  const providers = useQuery(api['insurance/providers'].list, { orgId });
  const typeList = useMemo<IOrgInsuranceType[]>(() => orgTypes ?? [], [orgTypes]);
  const providerList = useMemo<IInsuranceProvider[]>(
    () => (providers ?? []) as IInsuranceProvider[], // AS_JUSTIFIED: Convex return matches IInsuranceProvider shape
    [providers]
  );

  useEffect(() => {
    if (step === 'review' && extractedData) {
      setPolicyNumber(extractedData.policyNumber);
      setCustomerName(extractedData.policyholder.name);

      if (!selectedTypeId && typeList.length > 0) {
        if (extractedData.vehicle) {
          const carType = findCarInsuranceType(typeList);
          if (carType) setTypeId(carType._id);
        }
        if (typeList.length === 1) setTypeId(typeList[0]._id);
      }
    }
  }, [step, extractedData, typeList, selectedTypeId, setPolicyNumber, setCustomerName, setTypeId]);

  const handleClose = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        reset();
        resetForm();
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, reset, resetForm]
  );

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      addFiles(Array.from(files));
    },
    [addFiles]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect]
  );

  const handleStartRecognition = useCallback(() => {
    if (pendingFiles.length === 0) return;
    void uploadFiles(pendingFiles);
  }, [pendingFiles, uploadFiles]);

  const handleSave = useCallback(async () => {
    if (!selectedTypeId || !policyNumber.trim() || !customerName.trim() || !extractedData) return;

    const fieldValues = buildFieldValues(extractedData, providerList);

    const policyId = await savePolicy(
      selectedTypeId as Id<'orgInsuranceTypes'>, // AS_JUSTIFIED: value comes from Select populated with IOrgInsuranceType IDs
      policyNumber.trim(),
      customerName.trim(),
      fieldValues,
      'active'
    );
    if (policyId) {
      handleClose(false);
      navigate(`/insurance/policies/${policyId}`);
    }
  }, [
    selectedTypeId,
    policyNumber,
    customerName,
    extractedData,
    providerList,
    savePolicy,
    handleClose,
    navigate,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" aria-hidden="true" />
            Import Policy
          </DialogTitle>
          <DialogDescription>
            Upload policy documents, then start recognition to extract data with AI.
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="flex flex-col gap-4">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={e => {
                handleFileSelect(e.target.files);
                e.target.value = '';
              }}
            />
            <div
              role="button"
              tabIndex={0}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={e => e.key === 'Enter' && fileInputRef.current?.click()}
              className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border',
                'bg-card py-8 px-6 text-center cursor-pointer hover:bg-muted/50 transition-colors'
              )}
            >
              <Upload className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">Click or drag files to upload</p>
                <p className="text-xs text-muted-foreground mt-1">
                  PDF, JPG, PNG — add files one by one or multiple at once
                </p>
              </div>
            </div>

            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-foreground">
                  Selected files ({pendingFiles.length})
                </p>
                {pendingFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${file.size}`}
                    className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText
                        className="w-4 h-4 text-muted-foreground shrink-0"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-foreground truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {(file.size / 1024).toFixed(0)} KB
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 shrink-0"
                      onClick={e => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleStartRecognition} disabled={pendingFiles.length === 0}>
                <Play className="w-4 h-4 mr-2" aria-hidden="true" />
                Start Recognition
              </Button>
            </div>
          </div>
        )}

        {step === 'extracting' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 animate-pulse" aria-hidden="true" />
              <span>Analyzing documents...</span>
            </div>
            {uploadedFileNames.map(name => (
              <div key={name} className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                <span className="truncate">{name}</span>
              </div>
            ))}
            <div className="space-y-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </div>
          </div>
        )}

        {step === 'review' && extractedData && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" aria-hidden="true" />
                Imported with AI
              </Badge>
              <span className="text-xs text-muted-foreground">
                Review and confirm before saving
              </span>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="import-type" className="text-[13px] font-medium text-foreground">
                  Insurance Type <span className="text-destructive">*</span>
                </label>
                <Select value={selectedTypeId} onValueChange={setTypeId}>
                  <SelectTrigger id="import-type" className="!h-10">
                    <SelectValue placeholder="Select insurance type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeList.map(t => (
                      <SelectItem key={t._id} value={t._id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="import-policy-number"
                  className="text-[13px] font-medium text-foreground"
                >
                  Policy Number <span className="text-destructive">*</span>
                </label>
                <Input
                  id="import-policy-number"
                  value={policyNumber}
                  onChange={e => setPolicyNumber(e.target.value)}
                  className="!h-10"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="import-customer-name"
                  className="text-[13px] font-medium text-foreground"
                >
                  Customer Name <span className="text-destructive">*</span>
                </label>
                <Input
                  id="import-customer-name"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  className="!h-10"
                />
              </div>

              {/* Extracted data summary */}
              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5">
                <p className="text-xs font-medium text-foreground">Extracted Details</p>
                {extractedData.vehicle && (
                  <p className="text-xs text-muted-foreground">
                    Vehicle:{' '}
                    {[
                      extractedData.vehicle.make,
                      extractedData.vehicle.model,
                      extractedData.vehicle.registrationNumber,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                )}
                {extractedData.insurance.startDate && (
                  <p className="text-xs text-muted-foreground">
                    Period: {extractedData.insurance.startDate} —{' '}
                    {extractedData.insurance.expiryDate ?? '?'}
                  </p>
                )}
                {extractedData.premium?.totalPremium && (
                  <p className="text-xs text-muted-foreground">
                    Premium: {extractedData.premium.currency ?? 'EUR'}{' '}
                    {extractedData.premium.totalPremium}
                  </p>
                )}
                {extractedData.insurer?.name && (
                  <p className="text-xs text-muted-foreground">
                    Insurer: {extractedData.insurer.name}
                  </p>
                )}
                {extractedData.drivers && extractedData.drivers.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Drivers: {extractedData.drivers.map(d => d.name).join(', ')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleSave()}
                disabled={!selectedTypeId || !policyNumber.trim() || !customerName.trim()}
              >
                Save Policy
              </Button>
            </div>
          </div>
        )}

        {step === 'saving' && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="w-4 h-4 animate-pulse" aria-hidden="true" />
              <span>Saving policy...</span>
            </div>
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {step === 'error' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-3">
              <AlertCircle
                className="w-4 h-4 text-destructive shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-destructive">{error ?? 'An unexpected error occurred'}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Close
              </Button>
              <Button onClick={reset}>Try Again</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
